import React, { useState, useRef, useEffect } from 'react';
import {
  A,
  StyledLink,
  StyledButton,
  StyledWhiteIconButton,
  StyledWhiteButton,
  StyledPrimaryButton,
  StyledWarnButton,
  StyledOutlineButton,
  StyledHoverWarnOutlineButton,
  StyledPrimaryOutlineButton,
  StyledWhiteOutlineButton,
  StyledTextButton,
  StyledFacebookButton,
  StyledTwitterButton,
  StyledBlockButton,
} from './style';
import styled from 'styled-components';
import { MEDIA_BREAK } from 'src/components/layout';

import Icon from 'src/components/icon';

const handleLinkWrapping = (Component, props, ref) => {
  const { href, to, target, children, disabled, isLoading, ...rest } = props;
  const button = (
    <Component disabled={disabled || isLoading} ref={ref} {...rest}>
      {children}
    </Component>
  );

  if (href)
    return (
      <A
        href={href}
        target={target || '_blank'}
        rel={!target ? 'noopener noreferrer' : undefined}
      >
        {button}
      </A>
    );
  if (to) return <StyledLink to={to}>{button}</StyledLink>;
  return button;
};

export const Button = props => handleLinkWrapping(StyledButton, props);

export const WhiteIconButton = props =>
  handleLinkWrapping(StyledWhiteIconButton, props);

export const WhiteButton = props =>
  handleLinkWrapping(StyledWhiteButton, props);

export const PrimaryButton = props =>
  handleLinkWrapping(StyledPrimaryButton, props);

export const WarnButton = props => handleLinkWrapping(StyledWarnButton, props);

export const OutlineButton = props =>
  handleLinkWrapping(StyledOutlineButton, props);

export const PrimaryOutlineButton = props =>
  handleLinkWrapping(StyledPrimaryOutlineButton, props);

export const WhiteOutlineButton = props =>
  handleLinkWrapping(StyledWhiteOutlineButton, props);

export const HoverWarnOutlineButton = props =>
  handleLinkWrapping(StyledHoverWarnOutlineButton, props);

export const TextButton = props => handleLinkWrapping(StyledTextButton, props);

export const FacebookButton = props =>
  handleLinkWrapping(StyledFacebookButton, props);

export const TwitterButton = props =>
  handleLinkWrapping(StyledTwitterButton, props);

export const BlockButton = props =>
  handleLinkWrapping(StyledBlockButton, props);

const DropDown = styled.div`
  position: absolute;
  padding-top: 17px;
  width: ${props => props.width};
  display: ${props => (props.show ? 'inline-block' : 'none')};
  left: ${props => props.left};
  z-index: 1;
`;

const Options = styled.div`
  position: absolute;
  height: 65px;
  width: ${props => props.width};
  padding: 27px 10px 7px 10px;
  margin-top: -37px;
  border-radius: 0px 0px 7px 7px;
  background-color: #fff;
  color: #454bc9;
  border: 1px solid #e2e7ed;
  display: ${props => (props.show ? 'block' : 'none')};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
`;

const AbsoluteButton = styled(PrimaryButton)`
  position: absolute;
  margin-top: -58px;
  min-width: 110px;
  width: ${props => props.width};
  display: ${props => (props.show ? 'block' : 'none')};
  &:hover {
    background-color: #454bc9;
  }
  transition: all 0.3s;
`;

const BaseButton = styled(PrimaryOutlineButton)`
  transition: all 0.3s;
  width: ${props => props.width};
  height: 40px;

  @media (max-width: ${MEDIA_BREAK}px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const RsvpButton = props => {
  const {
    options,
    children,
    width = '126px',
    rsvp,
    dropDownleft,
    ...rest
  } = props;
  const firstOption = options && options.length ? options[0] : {};
  const restOptions = options && options.length ? options.slice(1) : [];
  const sectionRef = useRef();
  const [showDropDown, setShowDropDown] = useState(false);
  const handleClickOutside = e => {
    if (e.target !== sectionRef.current) {
      setShowDropDown(false);
    }
  };

  useEffect(() => {
    window.document.addEventListener('click', handleClickOutside, true);
    return () => {
      window.document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <React.Fragment>
      <BaseButton
        width={width}
        ref={sectionRef}
        onClick={e => {
          if (rsvp === 'GOING') {
            e.preventDefault();
            setShowDropDown(true);
          } else {
            firstOption.listener(e);
          }
        }}
        {...rest}
      >
        {children}
      </BaseButton>
      <DropDown
        width={width}
        show={showDropDown}
        top={window.top}
        left={dropDownleft ? dropDownleft : 'auto'}
      >
        {restOptions.map(ro => (
          <Options
            width={width}
            show={showDropDown}
            key={ro.name}
            onClick={ro.listener}
          >
            {ro.name}
          </Options>
        ))}
        <AbsoluteButton
          width={width}
          show={showDropDown}
          onClick={firstOption.listener}
        >
          {firstOption.name}
        </AbsoluteButton>
      </DropDown>
    </React.Fragment>
  );
};

export const BasePlayBackButton = styled(PrimaryButton)`
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 15px;
  padding-right: 15px;
  width: ${props => props.width};
  @media (max-width: ${MEDIA_BREAK}px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const PlayBackButton = props => {
  const { children, ...rest } = props;
  return (
    <BasePlayBackButton {...rest}>
      <Icon size={40} color="#fff" glyph="play-circle" />
      {children}
    </BasePlayBackButton>
  );
};

export const EventStatusButton = styled.div`
  width: ${props => (props.width ? props.width : '126px')};

  padding: 12px 25px;
  border-radius: 32px;
  background-color: #f3f6fa;

  font-family: Lato;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.22;
  letter-spacing: normal;
  text-align: left;
  color: ${props => (props.status === 'REJECTED' ? '#ed4b53' : '#464ac9')};

  @media (max-width: ${MEDIA_BREAK}px) {
    margin-left: auto;
    margin-right: auto;
  }
`;
